// @flow

import * as React from 'react';
import {useState} from 'react';

import Box from 'sail/Box';
import {Subheading} from 'sail/Text';

import {useNavigation} from 'docs/data';
import {SlidingPane} from 'docs/shell/mobile/helpers/SlidingPane';

import {MenuButton, BackButton} from 'docs/shell/mobile/helpers/MenuButton';
import {Search} from 'docs/shell/header/search/Search';

import './MobileNavOverlay.scss';

import type {NavigationTopic} from 'docs/data';

type MobileNavOverlayProps = {|
  onCloseNav: () => void,
|};

export const MobileNavOverlay = ({onCloseNav}: MobileNavOverlayProps) => {
  const [isTopNavClicked, setIsTopNavClicked] = useState(false);
  const {navigation} = useNavigation();

  const topics = navigation.topics;
  const activeTopic = topics.find((t) => t.active) || null;

  const [selectedTopic, setSelectedTopic] = useState<NavigationTopic | null>(
    activeTopic,
  );

  const setTopic = (topic) => {
    if (!isTopNavClicked) {
      setIsTopNavClicked(true);
    }
    setSelectedTopic(topic);
  };

  const mainNav = (
    <Box
      padding={{left: 24, right: 24, top: 20, bottom: 20}}
      flex={{direction: 'column'}}
    >
      {topics.map((topic) => {
        const hasMore = topic.sections.length > 0;
        const href = hasMore ? null : topic.href;
        const onClick = hasMore ? () => setTopic(topic) : onCloseNav;
        return (
          <MenuButton
            key={topic.text}
            label={topic.text}
            href={href}
            onClick={onClick}
            isRightArrowVisible={hasMore}
            isActive={location.pathname === href}
          />
        );
      })}
    </Box>
  );

  const subNav = (
    <MobileNavSection
      isTopNavClicked={isTopNavClicked}
      sectionTitle={selectedTopic?.text || ''}
      sectionHref={selectedTopic?.href}
      onBackClick={() => setSelectedTopic(null)}
      onCloseNav={onCloseNav}
      sections={selectedTopic?.sections || []}
    />
  );
  return (
    <Box className="MobileNavOverlay">
      <Box
        padding={{left: 24, right: 24, top: 24, bottom: 4}}
        className="Search-container"
      >
        <Search isInputEndIconVisible={false} onResultClick={onCloseNav} />
      </Box>
      <SlidingPane
        leftPane={mainNav}
        rightPane={subNav}
        isLeftPaneVisible={
          selectedTopic == null || selectedTopic.sections?.length === 0
        }
        initial
      />
    </Box>
  );
};

const MobileNavSection = ({
  isTopNavClicked,
  onBackClick,
  onCloseNav,
  sectionHref,
  sectionTitle,
  sections,
}) => {
  const {navigation} = useNavigation();
  const activeProduct = navigation.products.find((t) => t.active);
  // Always open this section if the top nav was clicked to access this pane
  // Otherwise default to the active section if one is selected.
  const defaultDetailsSection = isTopNavClicked
    ? null
    : sections.find((section) => section === activeProduct?.text) || null;
  const [detailsSection, setDetailsSection] = useState<string | null>(
    defaultDetailsSection,
  );
  const products =
    navigation.products.filter((p) => p.topic?.href === sectionHref) || [];
  const leftPane = (
    <Box
      padding={{left: 24, right: 24, top: 20, bottom: 20}}
      flex={{direction: 'column'}}
    >
      <BackButton label={sectionTitle} onClick={onBackClick} />
      <Box flex={{direction: 'column'}}>
        <Box margin={{left: 48}}>
          <MenuButton
            label="Overview"
            href={sectionHref}
            onClick={onCloseNav}
            isRightArrowVisible={false}
            isActive={location.pathname === sectionHref}
          />
        </Box>
        {products.map((product) => {
          return (
            <>
              <Box margin={{left: 48}}>
                <MenuButton
                  label={product.text}
                  key={product.text}
                  isRightArrowVisible
                  onClick={() => setDetailsSection(product.text)}
                />
              </Box>
              {product.icon === 'connect' ? <Spacer /> : null}
            </>
          );
        })}
      </Box>
    </Box>
  );
  const rightPane = (
    <MobileNavDetails
      activeTopicName={detailsSection || ''}
      onBackClick={() => setDetailsSection(null)}
      onCloseNav={onCloseNav}
    />
  );
  return (
    <SlidingPane
      isLeftPaneVisible={detailsSection == null}
      leftPane={leftPane}
      rightPane={rightPane}
      initial={false}
    />
  );
};

const Spacer = () => <div className="MobileNavOverlay-Spacer" />;

const MobileNavDetails = ({activeTopicName, onBackClick, onCloseNav}) => {
  const {navigation} = useNavigation();
  const selectedProduct = navigation.products.find(
    (p) => p.text === activeTopicName,
  );
  return (
    <Box
      padding={{left: 24, right: 24, top: 20, bottom: 20}}
      flex={{direction: 'column'}}
    >
      <BackButton label={activeTopicName} onClick={onBackClick} />
      <Box margin={{left: 48}} flex={{direction: 'column'}}>
        {(selectedProduct?.children || []).map((product) => {
          if (product.type === 'heading') {
            return <HeaderSection key={product.text} label={product.text} />;
          }
          return (
            <MenuButton
              key={product.text}
              label={product.text}
              href={product.href}
              isRightArrowVisible={false}
              onClick={onCloseNav}
              isActive={location.pathname === product.href}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const HeaderSection = ({label}) => {
  return (
    <Box margin={{top: 16}} className="MobileNavOverlay-Header">
      <Subheading color="gray500">{label}</Subheading>
    </Box>
  );
};
